import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Pagination from "@mui/material/Pagination";

import { get } from "lib/api";
import StatusChip from "components/StatusChip";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosCard, IosFilterBar, IosPage, iosColors } from "mobile/components/IosParity";

const normalize = (d) =>
  d?.attributes ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes } : { ...d, documentId: d.documentId || d.id };

const primaryActionSx = {
  bgcolor: "#B54708",
  color: "#fff",
  borderRadius: 1,
  minHeight: 38,
  fontWeight: 900,
  textTransform: "none",
  boxShadow: "none",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  "&:hover": { bgcolor: "#953A05", boxShadow: "none" },
  "& .MuiButton-startIcon": { flexShrink: 0 },
};

const secondaryActionSx = {
  bgcolor: "rgba(255,106,0,0.07)",
  color: "#8F3700",
  borderColor: "rgba(181,71,8,0.38)",
  borderRadius: 1,
  minHeight: 38,
  fontWeight: 900,
  textTransform: "none",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  "&:hover": { bgcolor: "rgba(255,106,0,0.12)", borderColor: "rgba(181,71,8,0.52)" },
  "& .MuiButton-startIcon": { flexShrink: 0 },
};

export default function PipelineMobile() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(null);
  const [workOrderTab, setWorkOrderTab] = useState("active");
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const { data, isLoading, error } = useQuery({
    queryKey: ["m-pipeline-work-orders", workOrderTab, page, pageSize],
    queryFn: () =>
      get("/api/work-orders", {
        filters: workOrderTab === "completed"
          ? { status: { $eq: "complete" } }
          : { status: { $ne: "complete" } },
        populate: {
          items: { populate: { inventoryItem: true, buildSheet: true } },
          units: { populate: { workOrderItem: true } },
          inventoryOrders: true,
        },
        sort: "updatedAt:desc",
        pagination: { page, pageSize },
      }),
    staleTime: 5_000,
  });

  const workOrders = useMemo(() => (data?.data || []).map(normalize), [data]);
  const pageCount = data?.meta?.pagination?.pageCount || 1;
  const pipelineSubtitle = workOrderTab === "completed" ? "Completed work orders" : "Active work orders";

  const getExistingForLine = (wo, line) => {
    const units = (wo?.units?.data || wo?.units || []).map(normalize);
    const lineDocId = line.documentId || line.id;
    const byLine = units.filter((u) => {
      const unitLineId = u.workOrderItem?.documentId || u.workOrderItem?.id;
      return unitLineId === lineDocId;
    });
    if (byLine.length > 0) return byLine.length;
    const sku = line.inventoryItem?.sku;
    if (!sku) return 0;
    return units.filter((u) => u.finishedGood?.sku === sku).length;
  };

  const getInventoryOrdersForWorkOrder = (wo) => {
    const raw = wo?.inventoryOrders?.data || wo?.inventoryOrders || [];
    const rows = (Array.isArray(raw) ? raw : []).map(normalize);
    return rows.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  };

  return (
    <IosPage>
      <MobileTopBar title="Pipeline" subtitle={pipelineSubtitle} onBack={() => navigate("/m/dashboard")} />

      <IosFilterBar
        label="Work orders"
        value={workOrderTab}
        options={[
          { key: "active", label: "Active", icon: "play_circle", accent: "orange" },
          { key: "completed", label: "Completed", icon: "check_circle", accent: "green" },
        ]}
        onChange={(nextValue) => {
          setWorkOrderTab(nextValue);
          setExpanded(null);
          setPage(1);
        }}
      />

      {isLoading ? <LinearProgress sx={{ my: 1 }} /> : null}
      {error ? (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          Failed to load pipeline.
        </Typography>
      ) : null}

      <Stack spacing={0.75}>
        {workOrders.map((wo) => {
          const woId = wo.documentId;
          const units = (wo.units?.data || wo.units || []).map(normalize);
          const items = (wo.items?.data || wo.items || []).map(normalize);
          const inventoryOrders = getInventoryOrdersForWorkOrder(wo);
          const rowKey = woId || wo.id;
          const isOpen = expanded === rowKey;

          return (
            <Accordion
              key={rowKey}
              expanded={isOpen}
              onChange={() => setExpanded((p) => (p === rowKey ? null : rowKey))}
              disableGutters
              sx={{
                border: `1px solid ${iosColors.line}`,
                borderRadius: 1,
                boxShadow: `0 12px 24px ${iosColors.shadow}`,
                overflow: "hidden",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.75} sx={{ width: "100%" }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={900}>
                      {wo.workOrderNumber || "Work Order"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {wo.customerRef || "—"}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap justifyContent="flex-end">
                    <StatusChip value={wo.status || "active"} />
                    <Chip size="small" variant="outlined" label={`${units.length} units`} />
                  </Stack>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1, pt: 0, pb: 1.5 }}>
                <Stack direction="row" spacing={0.75} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Icon>build</Icon>}
                    disabled={!woId}
                    onClick={() => {
                      if (woId) navigate(`/m/work-orders/${woId}`);
                    }}
                    sx={secondaryActionSx}
                  >
                    Manage
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Icon>inventory_2</Icon>}
                    disabled={!woId}
                    onClick={() => {
                      if (woId) navigate(`/m/pipeline/work-orders/${woId}?from=${workOrderTab}`);
                    }}
                    sx={primaryActionSx}
                  >
                    Build / Packaging
                  </Button>
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: "block", fontWeight: 900, textTransform: "uppercase" }}>
                  Inventory orders
                </Typography>
                <Stack spacing={0.6} sx={{ mb: 1.25 }}>
                  {inventoryOrders.length ? (
                    inventoryOrders.map((order) => (
                      <IosCard
                        key={order.documentId || order.id}
                        sx={{ width: "100%", minHeight: 0, p: 1, boxShadow: "none" }}
                      >
                        <Stack direction="row" spacing={0.75} justifyContent="space-between" alignItems="center">
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={900}>
                              {order.orderNumber || order.documentId || order.id}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Component order • {order.status || "-"}
                            </Typography>
                          </Box>
                          <Icon fontSize="small" sx={{ color: iosColors.subtle }}>assignment</Icon>
                        </Stack>
                      </IosCard>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No inventory orders yet. Create component orders from the valve body detail.
                    </Typography>
                  )}
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: "block" }}>
                  Generated tags
                </Typography>
                <Stack spacing={0.75}>
                  {units.map((u) => {
                    const unitId = u.documentId;
                    return (
                      <ButtonBase
                        key={unitId || u.id}
                        onClick={() => {
                          if (unitId) navigate(`/m/internal/valve-bodies/${unitId}`);
                        }}
                        sx={{ width: "100%", textAlign: "left", borderRadius: 1 }}
                      >
                        <IosCard sx={{ width: "100%", minHeight: 0, p: 1.35, boxShadow: "none" }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.75}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={900}>
                                {u.serialNumber || u.optionalTag || `Unit ${unitId || u.id || ""}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Open valve body detail
                              </Typography>
                            </Box>
                            <Icon fontSize="small">chevron_right</Icon>
                          </Stack>
                        </IosCard>
                      </ButtonBase>
                    );
                  })}
                  {!units.length ? (
                    <Typography variant="body2" color="text.secondary">
                      No units generated yet.
                    </Typography>
                  ) : null}
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1.25, mb: 0.75, display: "block" }}>
                  Line items
                </Typography>
                <Stack spacing={0.75}>
                  {items.map((it) => {
                    const inv = it?.inventoryItem?.data ? normalize(it.inventoryItem.data) : it?.inventoryItem ? normalize(it.inventoryItem) : null;
                    const line = { ...it, inventoryItem: inv || it.inventoryItem };
                    const qty = Number(it.quantity || 0);
                    const existing = getExistingForLine(wo, line);
                    const remaining = Math.max(0, qty - existing);
                    const lineDocId = it.documentId || it.id;
                    const lineTags = units.filter((u) => {
                      const unitLineId = u.workOrderItem?.documentId || u.workOrderItem?.id;
                      return unitLineId === lineDocId;
                    });
                    return (
                      <IosCard key={it.documentId || it.id} sx={{ minHeight: 0, p: 1.35, boxShadow: "none" }}>
                        <Stack spacing={0.75}>
                          <Stack direction="row" spacing={0.75} justifyContent="space-between" alignItems="flex-start">
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={900}>
                                {inv?.sku || inv?.name || "Item"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {inv?.name || "No item name"}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                              <Chip size="small" variant="outlined" label={`Qty ${qty}`} />
                              <Chip size="small" variant="outlined" label={`Tags ${existing}/${qty}`} />
                            </Stack>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {remaining > 0
                              ? `${remaining} tag${remaining === 1 ? "" : "s"} still need to be generated from Build / Packaging.`
                              : "All tags for this line have been generated."}
                          </Typography>
                          {lineTags.length ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {lineTags.map((tag) => (
                                <Chip
                                  key={tag.documentId || tag.id}
                                  clickable
                                  icon={<Icon sx={{ fontSize: 16 }}>sell</Icon>}
                                  label={tag.serialNumber || tag.optionalTag || tag.documentId || tag.id}
                                  color="info"
                                  onClick={() => {
                                    if (tag.documentId) navigate(`/m/internal/valve-bodies/${tag.documentId}`);
                                  }}
                                  sx={{ fontWeight: 900 }}
                                />
                              ))}
                            </Stack>
                          ) : null}
                        </Stack>
                      </IosCard>
                    );
                  })}
                  {!items.length ? (
                    <Typography variant="body2" color="text.secondary">
                      No items added to this work order yet.
                    </Typography>
                  ) : null}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>

      {pageCount > 1 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <Pagination
            size="small"
            count={pageCount}
            page={page}
            onChange={(e, p) => setPage(p)}
            sx={{
              "& .MuiPaginationItem-root": {
                fontWeight: 900,
                borderRadius: 1,
              },
              "& .Mui-selected": {
                bgcolor: "#B54708 !important",
                color: "#fff",
              },
            }}
          />
        </Box>
      ) : null}
    </IosPage>
  );
}
